import { useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  ArrowLeftIcon,
  CaretRightIcon,
  CheckIcon,
  InfoIcon,
  MinusIcon,
  XIcon,
} from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/drawer';
import { SearchBar } from '@/components/ui/search-bar';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { getGroupRoleTone } from '@/lib/utils/groups.utils';
import { getRoleLabelByKey } from '@/lib/utils/roles.utils';
import {
  getBulkGroupImpact,
  groupBulkGroupsForPicker,
} from '@/lib/utils/bulk-edit-users.utils';
import { ManageUsersContent } from '@/content/ManageUsersContent';
import type { MembershipsByUser } from '@/lib/types/bulk-edit-users.types';
import type { GroupWithMemberCount, UserRole } from '@/lib/types/database';

type BulkAddGroupsDrawerStep = 'pick' | 'confirm';

interface BulkAddGroupsDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedUserIds: string[];
  groups: GroupWithMemberCount[];
  selectedGroupIds: Set<string>;
  searchQuery: string;
  membershipsByUser: MembershipsByUser;
  isLoadingGroups: boolean;
  isSubmitting: boolean;
  onSearchQueryChange: (value: string) => void;
  onSelectedGroupIdsChange: (value: Set<string>) => void;
  onConfirm: () => void;
}

export function BulkAddGroupsDrawer({
  open,
  onOpenChange,
  selectedUserIds,
  groups,
  selectedGroupIds,
  searchQuery,
  membershipsByUser,
  isLoadingGroups,
  isSubmitting,
  onSearchQueryChange,
  onSelectedGroupIdsChange,
  onConfirm,
}: BulkAddGroupsDrawerProps) {
  const [step, setStep] = useState<BulkAddGroupsDrawerStep>('pick');
  const content = ManageUsersContent.bulkEdit.addGroupsDrawer;
  const selectedUserCount = selectedUserIds.length;
  const filteredGroups = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return groups;
    return groups.filter((group) =>
      group.display_name.toLowerCase().includes(query)
    );
  }, [groups, searchQuery]);
  const sections = useMemo(
    () => groupBulkGroupsForPicker(filteredGroups),
    [filteredGroups]
  );
  const impact = useMemo(
    () =>
      getBulkGroupImpact(
        selectedUserIds,
        groups,
        selectedGroupIds,
        membershipsByUser
      ),
    [groups, membershipsByUser, selectedGroupIds, selectedUserIds]
  );
  const canReview = selectedUserCount > 0 && selectedGroupIds.size > 0;

  useEffect(() => {
    if (open) {
      setStep('pick');
    }
  }, [open]);

  const toggleGroup = (groupId: string) => {
    const next = new Set(selectedGroupIds);
    if (next.has(groupId)) {
      next.delete(groupId);
    } else {
      next.add(groupId);
    }
    onSelectedGroupIdsChange(next);
  };

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent className="max-h-[88vh] rounded-t-[22px]">
        <div className="mx-auto flex min-h-0 w-full max-w-sm flex-col">
          {step === 'pick' ? (
            <>
              <DrawerHeader className="pb-3">
                <DrawerHeaderRow
                  title={content.title}
                  description={`${content.selectedUsers(selectedUserCount)} · ${content.description}`}
                />
              </DrawerHeader>

              <div className="mb-3 flex gap-2 rounded-xl bg-green-400/15 p-3 text-small text-green-200">
                <InfoIcon
                  size={16}
                  weight="bold"
                  className="mt-0.5 shrink-0 text-green-300"
                />
                <span>{content.additiveOnly}</span>
              </div>

              <SearchBar
                value={searchQuery}
                onChange={onSearchQueryChange}
                placeholder={content.searchPlaceholder}
                className="mb-3 [&_input]:border-slate-500 [&_input]:bg-slate-600 [&_input]:text-slate-50 [&_input]:placeholder:text-slate-500"
              />

              <div className="h-44 overflow-y-auto pr-1">
                {isLoadingGroups ? (
                  <BulkGroupsSkeleton />
                ) : sections.length === 0 ? (
                  <div className="rounded-xl border border-slate-600 bg-slate-600/40 px-4 py-8 text-center text-body text-slate-400">
                    {searchQuery.trim() ? content.searchEmpty : content.empty}
                  </div>
                ) : (
                  <div className="space-y-4">
                    {sections.map((section) => (
                      <BulkGroupSection
                        key={section.parentRole}
                        parentRole={section.parentRole}
                        groups={section.groups}
                        selectedGroupIds={selectedGroupIds}
                        membershipsByUser={membershipsByUser}
                        selectedUserIds={selectedUserIds}
                        onToggleGroup={toggleGroup}
                      />
                    ))}
                  </div>
                )}
              </div>

              <div className="mt-4 rounded-xl border border-purple-300/20 bg-slate-600 p-3">
                <div className="mb-1 font-field-42 text-[10px] uppercase text-purple-200">
                  {content.willApply}
                </div>
                <p className="text-body text-slate-100">
                  {content.newMembershipSummary(
                    impact.newMembershipCount,
                    impact.selectedGroups.length
                  )}
                  {impact.unchangedMembershipCount > 0 && (
                    <span className="text-slate-400">
                      {' · '}
                      {content.unchangedSummary(
                        impact.unchangedMembershipCount
                      )}
                    </span>
                  )}
                </p>
                {impact.selectedGroups.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {impact.selectedGroups.map((group) => (
                      <GroupChip
                        key={group.id}
                        group={group}
                        onRemove={() => toggleGroup(group.id)}
                      />
                    ))}
                  </div>
                )}
              </div>

              <DrawerFooter className="pt-4">
                <Button
                  type="button"
                  size="lg"
                  onClick={() => setStep('confirm')}
                  disabled={!canReview}
                  className="bg-green-400 text-slate-700 hover:bg-green-300"
                >
                  {content.review}
                  <CaretRightIcon size={16} weight="bold" />
                </Button>
                <DrawerClose asChild>
                  <Button variant="outline" size="lg">
                    {content.cancel}
                  </Button>
                </DrawerClose>
              </DrawerFooter>
            </>
          ) : (
            <>
              <DrawerHeader className="pb-3">
                <DrawerHeaderRow title={content.confirmTitle} />
              </DrawerHeader>

              <div className="space-y-3">
                <p className="text-body text-slate-100">
                  {content.confirmSummary(
                    selectedUserCount,
                    impact.selectedGroups.length
                  )}
                </p>
                <div className="rounded-xl bg-slate-600 px-4 py-1">
                  {impact.rows.map((row, index) => (
                    <ImpactLine
                      key={row.group.id}
                      icon={<GroupDot group={row.group} />}
                      last={index === impact.rows.length - 1}
                    >
                      <span className="font-semibold text-slate-50">
                        {row.group.display_name}
                      </span>
                      {': '}
                      {content.groupImpact(
                        row.newMembershipCount,
                        row.alreadyAssignedCount
                      )}
                    </ImpactLine>
                  ))}
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <StatCard
                    value={impact.newMembershipCount}
                    label={content.newMemberships}
                    tone="green"
                  />
                  <StatCard value={0} label={content.removed} tone="muted" />
                </div>
              </div>

              <DrawerFooter className="pt-4">
                <Button
                  type="button"
                  size="lg"
                  onClick={onConfirm}
                  disabled={isSubmitting || !canReview}
                  className="bg-green-400 text-slate-700 hover:bg-green-300"
                >
                  {isSubmitting ? content.loading : content.confirm}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="lg"
                  onClick={() => setStep('pick')}
                  disabled={isSubmitting}
                >
                  <ArrowLeftIcon size={16} weight="bold" />
                  {content.back}
                </Button>
              </DrawerFooter>
            </>
          )}
        </div>
      </DrawerContent>
    </Drawer>
  );
}

interface DrawerHeaderRowProps {
  title: string;
  description?: string;
}

function DrawerHeaderRow({ title, description }: DrawerHeaderRowProps) {
  return (
    <div className="flex items-start justify-between gap-4">
      <div>
        <DrawerTitle>{title}</DrawerTitle>
        {description && (
          <DrawerDescription className="text-small text-slate-300">
            {description}
          </DrawerDescription>
        )}
      </div>
      <DrawerClose asChild>
        <button
          type="button"
          className="rounded-full p-1 text-slate-400 hover:text-slate-50"
          aria-label="Close add groups"
        >
          <XIcon size={18} weight="bold" />
        </button>
      </DrawerClose>
    </div>
  );
}

function BulkGroupsSkeleton() {
  return (
    <div className="space-y-3">
      {[...Array(3)].map((_, index) => (
        <div key={index} className="space-y-2">
          <Skeleton className="h-5 w-32 rounded-full bg-slate-600" />
          <Skeleton className="h-12 w-full rounded-xl bg-slate-600" />
        </div>
      ))}
    </div>
  );
}

interface BulkGroupSectionProps {
  parentRole: UserRole;
  groups: GroupWithMemberCount[];
  selectedGroupIds: Set<string>;
  selectedUserIds: string[];
  membershipsByUser: MembershipsByUser;
  onToggleGroup: (groupId: string) => void;
}

function BulkGroupSection({
  parentRole,
  groups,
  selectedGroupIds,
  selectedUserIds,
  membershipsByUser,
  onToggleGroup,
}: BulkGroupSectionProps) {
  const tone = getGroupRoleTone(parentRole);

  return (
    <section>
      <span
        className={cn(
          'mb-2 inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 font-field-42 text-[10px] uppercase text-slate-300',
          tone.pillClassName
        )}
      >
        <span className="h-1 w-1 rounded-full bg-current" />
        {getRoleLabelByKey(parentRole)}
      </span>
      <div className="overflow-hidden rounded-xl bg-slate-600">
        {groups.map((group, index) => {
          const checked = selectedGroupIds.has(group.id);
          const alreadyAssigned = selectedUserIds.filter((userId) =>
            (membershipsByUser.get(userId) ?? []).some(
              (membership) => membership.group_id === group.id
            )
          ).length;

          return (
            <button
              key={group.id}
              type="button"
              onClick={() => onToggleGroup(group.id)}
              className={cn(
                'flex w-full items-center gap-3 px-3 py-3 text-left transition-colors hover:bg-slate-500/30',
                checked && 'bg-purple-300/15',
                index > 0 && 'border-t border-slate-550'
              )}
            >
              <Checkbox checked={checked} />
              <div className="min-w-0 flex-1">
                <div className="truncate text-body text-slate-50">
                  {group.display_name}
                </div>
                <div className="mt-0.5 text-small text-slate-500">
                  {ManageUsersContent.bulkEdit.addGroupsDrawer.memberCount(
                    Number(group.member_count)
                  )}
                </div>
              </div>
              {checked && (
                <span
                  className={cn(
                    'shrink-0 font-field-42 text-[11px]',
                    alreadyAssigned > 0 ? 'text-amber-300' : 'text-green-300'
                  )}
                >
                  {alreadyAssigned > 0
                    ? ManageUsersContent.bulkEdit.addGroupsDrawer.alreadyHave(
                        alreadyAssigned
                      )
                    : ManageUsersContent.bulkEdit.addGroupsDrawer.allNew}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </section>
  );
}

interface GroupChipProps {
  group: GroupWithMemberCount;
  onRemove: () => void;
}

function GroupChip({ group, onRemove }: GroupChipProps) {
  const tone = getGroupRoleTone(group.parent_role);

  return (
    <span
      className={cn(
        'inline-flex max-w-[180px] items-center gap-1.5 rounded-full border px-2.5 py-1 font-field-42 text-[11px]',
        tone.pillClassName
      )}
    >
      <span className="truncate">{group.display_name}</span>
      <button
        type="button"
        aria-label={`Remove ${group.display_name}`}
        onClick={onRemove}
        className="rounded-full text-current opacity-80 hover:opacity-100"
      >
        <XIcon size={11} weight="bold" />
      </button>
    </span>
  );
}

interface GroupDotProps {
  group: GroupWithMemberCount;
}

function GroupDot({ group }: GroupDotProps) {
  const tone = getGroupRoleTone(group.parent_role);

  return (
    <span
      className={cn(
        'mt-1 h-2.5 w-2.5 shrink-0 rounded-full',
        tone.radioDotClassName
      )}
    />
  );
}

interface ImpactLineProps {
  icon: ReactNode;
  children: ReactNode;
  last?: boolean;
}

function ImpactLine({ icon, children, last = false }: ImpactLineProps) {
  return (
    <div
      className={cn(
        'flex items-start gap-3 py-3 text-body text-slate-100',
        !last && 'border-b border-slate-550'
      )}
    >
      {icon}
      <span>{children}</span>
    </div>
  );
}

interface StatCardProps {
  value: number;
  label: string;
  tone: 'green' | 'muted';
}

function StatCard({ value, label, tone }: StatCardProps) {
  return (
    <div className="rounded-xl bg-slate-600 p-3">
      <div
        className={cn(
          'font-field-74 text-[24px] leading-6',
          tone === 'green' ? 'text-green-300' : 'text-slate-400'
        )}
      >
        {tone === 'green' ? (
          <span className="inline-flex items-center gap-1">
            <CheckIcon size={18} weight="bold" />
            {value}
          </span>
        ) : (
          <span className="inline-flex items-center gap-1">
            <MinusIcon size={18} weight="bold" />
            {value}
          </span>
        )}
      </div>
      <div className="mt-1 font-field-42 text-[10px] uppercase text-slate-400">
        {label}
      </div>
    </div>
  );
}
